<template>
  <div class="subs-page">

    <!-- Header -->
    <div class="page-header">
      <div class="page-header-inner">
        <div>
          <div class="page-title">Gestão de Subscrições</div>
          <div class="page-subtitle">Atribuição e controlo de subscrições</div>
        </div>
        <div class="header-actions">
          <button class="btn-payment" @click="openPayment()">
            <q-icon name="payments" size="16px" class="q-mr-xs" />
            Cobrar por M-Pesa
          </button>
          <button class="btn-primary" @click="openAssign">
            <q-icon name="add" size="16px" class="q-mr-xs" />
            Atribuir subscrição
          </button>
        </div>
      </div>
    </div>

    <div class="content-wrapper">

      <!-- Filters -->
      <div class="filters-row">
        <q-select
          v-model="filterStatus"
          :options="statusOptions"
          option-value="value"
          option-label="label"
          emit-value
          map-options
          label="Estado"
          outlined
          dense
          clearable
          class="filter-select"
          @update:model-value="loadSubs(1)"
        />
        <q-input
          v-model.number="filterExpiring"
          label="Expira nos próximos N dias"
          outlined
          dense
          type="number"
          clearable
          class="filter-input"
          @update:model-value="loadSubs(1)"
        />
        <button class="btn-refresh" @click="loadSubs(1)" :disabled="loading">
          <q-icon name="refresh" size="18px" :class="loading ? 'spin' : ''" />
        </button>
      </div>

      <!-- Loading -->
      <div v-if="loading">
        <div v-for="n in 5" :key="n" class="skeleton-row q-mb-sm">
          <q-skeleton type="rect" height="66px" style="border-radius:12px" />
        </div>
      </div>

      <!-- Empty -->
      <div v-else-if="subscriptions.length === 0" class="empty-state">
        <q-icon name="receipt_long" size="56px" color="grey-4" />
        <div class="empty-text">Nenhuma subscrição encontrada</div>
      </div>

      <!-- Subscriptions list -->
      <div v-else class="subs-list">
        <div class="subs-table-head">
          <div class="col-user">Utilizador</div>
          <div class="col-plan">Plano</div>
          <div class="col-centre">Estado</div>
          <div class="col-centre">Subscrição actual</div>
          <div class="col-centre">Início</div>
          <div class="col-centre">Fim</div>
          <div class="col-centre">Dias restantes</div>
          <div class="col-centre">Slots extra</div>
          <div class="col-centre">Atribuído por</div>
          <div class="col-payment">Último pagamento</div>
          <div class="col-payment-action">Iniciar cobrança</div>
          <div class="col-actions">Acções</div>
        </div>

        <div
          v-for="sub in subscriptions"
          :key="sub.id"
          class="sub-row"
          @click="openDetail(sub)"
        >
          <div class="col-user">
            <div class="user-name">{{ sub.user?.name || '—' }}</div>
            <div class="user-email">{{ sub.user?.email || '' }}</div>
          </div>
          <div class="col-plan">
            <div class="plan-name">{{ sub.plan_snapshot?.plan_name || sub.plan?.name || '—' }}</div>
          </div>
          <div class="col-centre">
            <span class="status-badge" :class="`status-badge--${sub.status}`">
              {{ statusLabel(sub.status, sub.is_queued) }}
            </span>
          </div>
          <div class="col-centre">
            <span class="status-badge" :class="sub.is_active ? 'status-badge--active' : 'status-badge--inactive'">
              {{ sub.is_active ? 'Activa' : 'Inactiva' }}
            </span>
          </div>
          <div class="col-centre text-caption text-grey-6">{{ formatDate(sub.starts_at) }}</div>
          <div class="col-centre text-caption text-grey-6">{{ formatDate(sub.ends_at) }}</div>
          <div class="col-centre">
            <span :class="sub.days_remaining <= 7 ? 'text-negative text-weight-bold' : 'text-grey-7'">
              {{ sub.days_remaining ?? '—' }}
            </span>
          </div>
          <div class="col-centre text-grey-7">{{ sub.extra_slots ?? 0 }}</div>
          <div class="col-centre text-caption text-grey-5">{{ sub.granted_by?.name || '—' }}</div>
          <div class="col-payment">
            <template v-if="sub.last_payment">
              <span class="status-badge" :class="`status-badge--${sub.last_payment.status}`">
                {{ paymentStatusLabel(sub.last_payment.status) }}
              </span>
              <div class="payment-amount">{{ formatAmount(sub.last_payment.amount, sub.last_payment.currency) }}</div>
              <div class="payment-reference">{{ sub.last_payment.reference || formatDate(sub.last_payment.paid_at || sub.last_payment.created_at) }}</div>
              <button
                v-if="sub.last_payment.status === 'pending' && sub.last_payment.reference"
                class="payment-check-btn"
                type="button"
                :disabled="checkingPaymentReference === sub.last_payment.reference"
                @click.stop="checkPaymentStatus(sub.last_payment.reference)"
              >
                {{ checkingPaymentReference === sub.last_payment.reference ? 'A verificar…' : 'Verificar estado' }}
              </button>
            </template>
            <span v-else class="text-caption text-grey-5">Sem pagamentos</span>
          </div>
          <div class="col-payment-action" @click.stop>
            <button class="payment-start-btn" type="button" @click="openPayment(sub)">
              <q-icon name="payments" size="16px" />
              <span>Cobrar</span>
            </button>
          </div>
          <div class="col-actions" @click.stop>
            <button class="action-icon action-icon--extend" @click="openExtend(sub)">
              <q-icon name="update" size="17px" />
              <q-tooltip>Estender</q-tooltip>
            </button>
            <button
              v-if="sub.status !== 'cancelled'"
              class="action-icon action-icon--danger"
              @click="confirmCancel(sub)"
            >
              <q-icon name="cancel" size="17px" />
              <q-tooltip>Cancelar</q-tooltip>
            </button>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="meta && meta.last_page > 1" class="pagination-row">
        <q-pagination
          v-model="currentPage"
          :max="meta.last_page"
          :max-pages="6"
          direction-links
          boundary-links
          color="primary"
          active-design="unelevated"
          active-color="primary"
          active-text-color="white"
          @update:model-value="loadSubs"
        />
      </div>
    </div>

    <!-- ── Admin M-Pesa payment modal ───────────────────── -->
    <q-dialog v-model="paymentDialog" persistent>
      <q-card class="payment-dialog">
        <q-card-section class="dialog-header row items-center">
          <div class="dialog-title">Iniciar pagamento M-Pesa</div>
          <q-space />
          <q-btn flat round dense icon="close" color="grey-5" :disable="paymentSubmitting" @click="closePaymentDialog" />
        </q-card-section>

        <q-card-section class="q-pa-lg">
          <q-select
            v-model="paymentForm.user"
            use-input
            input-debounce="200"
            :options="filteredUsers"
            :option-label="userLabel"
            option-value="id"
            label="Cliente *"
            outlined
            dense
            class="q-mb-md"
            :disable="paymentSubmitting || paymentStatus === 'pending' || paymentStatus === 'paid'"
            @filter="filterUsers"
            @update:model-value="onPaymentUserSelected"
          />

          <q-select
            v-model="paymentForm.plan"
            :options="paymentPlans"
            option-label="name"
            option-value="id"
            label="Plano *"
            outlined
            dense
            class="q-mb-md"
            :disable="paymentSubmitting || paymentStatus === 'pending' || paymentStatus === 'paid'"
            @update:model-value="onPaymentPlanSelected"
          />

          <q-select
            v-model="paymentForm.duration_days"
            :options="paymentDurationOptions"
            emit-value
            map-options
            label="Duração *"
            outlined
            dense
            class="q-mb-md"
            :disable="!paymentForm.plan || paymentPlanPricesLoading || paymentSubmitting || paymentStatus === 'pending' || paymentStatus === 'paid'"
          />

          <q-input
            v-model="paymentForm.msisdn"
            label="Número M-Pesa do cliente *"
            hint="Formato moçambicano: 84XXXXXXX ou 25884XXXXXXX"
            outlined
            dense
            class="q-mb-md"
            :disable="paymentSubmitting || paymentStatus === 'pending' || paymentStatus === 'paid'"
          />

          <div v-if="paymentPrice" class="payment-amount-confirmation">
            <span>Valor a cobrar</span>
            <strong>{{ formatAmount(paymentPrice.price, paymentPrice.currency) }}</strong>
          </div>
          <div v-else class="text-caption text-grey-6 q-mb-md">
            Seleccione uma duração disponível para confirmar o valor configurado.
          </div>
          <q-checkbox
            v-if="paymentPrice"
            v-model="paymentForm.confirmAmount"
            :label="`Confirmo a cobrança de ${formatAmount(paymentPrice.price, paymentPrice.currency)} ao cliente`"
            color="positive"
            class="q-mb-md"
            :disable="paymentSubmitting || paymentStatus === 'pending' || paymentStatus === 'paid'"
          />

          <div
            v-if="paymentStatus"
            class="payment-result"
            :class="`payment-result--${paymentStatus}`"
            role="status"
            aria-live="polite"
          >
            <strong>{{ paymentStatusLabel(paymentStatus) }}</strong>
            <span>{{ paymentMessage }}</span>
            <span v-if="paymentReference">Referência: <b>{{ paymentReference }}</b></span>
            <span v-if="paymentAmount !== null">Valor: <b>{{ formatAmount(paymentAmount, paymentCurrency) }}</b></span>
            <button
              v-if="paymentStatus === 'pending' && paymentReference"
              class="payment-check-btn"
              type="button"
              :disabled="paymentPolling"
              @click="checkPaymentStatus(paymentReference)"
            >{{ paymentPolling ? 'A verificar…' : 'Verificar agora' }}</button>
            <button
              v-if="paymentStatus === 'unknown' || paymentStatus === 'failed'"
              class="payment-retry-btn"
              type="button"
              :disabled="paymentSubmitting"
              @click="retryAdminPayment"
            >{{ paymentStatus === 'failed' ? 'Iniciar nova tentativa' : 'Reenviar pedido com a mesma chave' }}</button>
          </div>
        </q-card-section>

        <q-card-actions align="right" class="q-pa-lg q-pt-none">
          <q-btn flat label="Fechar" color="grey-6" :disable="paymentSubmitting" @click="closePaymentDialog" />
          <q-btn
            v-if="!paymentStatus"
            unelevated
            label="Enviar cobrança"
            color="primary"
            :loading="paymentSubmitting"
            @click="submitAdminPayment"
            style="border-radius:8px"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- ── Assign modal ──────────────────────────────────── -->
    <q-dialog v-model="assignDialog" persistent>
      <q-card style="min-width:540px; max-width:620px; border-radius:18px;">
        <q-card-section class="dialog-header row items-center">
          <div class="dialog-title">Atribuir Subscrição</div>
          <q-space />
          <q-btn flat round dense icon="close" v-close-popup color="grey-5" />
        </q-card-section>

        <q-card-section class="q-pa-lg">
          <!-- User search -->
          <q-select
            v-model="assignForm.user"
            use-input
            input-debounce="200"
            :options="filteredUsers"
            :option-label="u => u.name + ' — ' + u.email"
            :option-value="u => u.id"
            label="Utilizador *"
            outlined
            dense
            class="q-mb-md"
            @filter="filterUsers"
            @update:model-value="assignForm.user_id = $event?.id"
          />

          <!-- Plan select -->
          <q-select
            v-model="assignForm.plan"
            :options="activePlans"
            option-label="name"
            option-value="id"
            label="Plano *"
            outlined
            dense
            class="q-mb-md"
            @update:model-value="onPlanSelected"
          />

          <!-- Duration (fixed plans) -->
          <div v-if="assignForm.plan && !assignForm.plan.is_negotiable" class="q-mb-md">
            <q-select
              v-model="assignForm.selectedPrice"
              :options="planPrices"
              :option-label="p => p.duration_days + ' dias — ' + p.price + ' ' + p.currency"
              label="Duração / Preço"
              outlined
              dense
              :loading="planPricesLoading"
              :disable="planPricesLoading"
              @update:model-value="prefillPrice"
            />
          </div>

          <!-- Manual dates (Enterprise) -->
          <div v-if="assignForm.plan?.is_negotiable" class="form-row-2 q-mb-md">
            <q-input v-model="assignForm.starts_at" label="Data início" outlined dense type="date" />
            <q-input v-model="assignForm.ends_at" label="Data fim" outlined dense type="date" />
          </div>

          <div class="form-row-2 q-mb-md">
            <q-input
              v-model.number="assignForm.price"
              :label="assignForm.plan?.is_negotiable ? 'Preço *' : 'Preço (editável)'"
              outlined
              dense
              type="number"
              :hint="assignForm.plan?.is_negotiable ? 'Obrigatório para planos Enterprise' : 'Pré-preenchido pelo plano'"
            />
            <q-input v-model="assignForm.currency" label="Moeda" outlined dense />
          </div>

          <q-input v-model.number="assignForm.extra_slots" label="Slots extra" outlined dense type="number" class="q-mb-md" />
          <q-input v-model="assignForm.notes" label="Notas internas" outlined dense type="textarea" rows="2" />
        </q-card-section>

        <q-card-actions align="right" class="q-pa-lg q-pt-none">
          <q-btn flat label="Cancelar" v-close-popup color="grey-6" />
          <q-btn
            unelevated
            label="Atribuir"
            color="primary"
            :loading="saving"
            @click="submitAssign"
            style="border-radius:8px"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- ── Extend modal ──────────────────────────────────── -->
    <q-dialog v-model="extendDialog" persistent>
      <q-card style="min-width:400px; border-radius:18px;">
        <q-card-section class="dialog-header row items-center">
          <div class="dialog-title">Estender Subscrição</div>
          <q-space />
          <q-btn flat round dense icon="close" v-close-popup color="grey-5" />
        </q-card-section>

        <q-card-section class="q-pa-lg">
          <div class="text-body2 text-grey-7 q-mb-sm">
            <strong>{{ selectedSub?.user?.name }}</strong> — {{ selectedSub?.plan_snapshot?.plan_name }}
          </div>
          <div class="text-caption text-grey-5 q-mb-md">
            Fim actual: {{ formatDate(selectedSub?.ends_at) }}
          </div>
          <q-input
            v-model.number="extendDays"
            label="Número de dias a adicionar *"
            outlined
            dense
            type="number"
            class="q-mb-sm"
          />
          <div v-if="newEndDate" class="new-end-date">
            <q-icon name="event" size="14px" class="q-mr-xs" />
            Nova data de fim: <strong>{{ newEndDate }}</strong>
          </div>
          <q-input v-model="extendNotes" label="Notas (opcional)" outlined dense type="textarea" rows="2" class="q-mt-md" />
        </q-card-section>

        <q-card-actions align="right" class="q-pa-lg q-pt-none">
          <q-btn flat label="Cancelar" v-close-popup color="grey-6" />
          <q-btn
            unelevated
            label="Confirmar extensão"
            color="primary"
            :loading="saving"
            @click="submitExtend"
            style="border-radius:8px"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- ── Detail panel ──────────────────────────────────── -->
    <q-dialog v-model="detailDialog">
      <q-card style="min-width:560px; max-width:660px; border-radius:18px;">
        <q-card-section class="dialog-header row items-center">
          <div class="dialog-title">Detalhe da Subscrição</div>
          <q-space />
          <q-btn flat round dense icon="close" v-close-popup color="grey-5" />
        </q-card-section>

        <q-card-section class="q-pa-lg" v-if="selectedSub">
          <div class="detail-grid">
            <div class="detail-item"><span class="detail-label">Utilizador</span><span class="detail-value">{{ selectedSub.user?.name }}</span></div>
            <div class="detail-item"><span class="detail-label">Email</span><span class="detail-value">{{ selectedSub.user?.email }}</span></div>
            <div class="detail-item"><span class="detail-label">Estado</span><span class="status-badge" :class="`status-badge--${selectedSub.status}`">{{ statusLabel(selectedSub.status, selectedSub.is_queued) }}</span></div>
            <div class="detail-item"><span class="detail-label">Plano</span><span class="detail-value">{{ selectedSub.plan_snapshot?.plan_name || selectedSub.plan?.name }}</span></div>
            <div class="detail-item"><span class="detail-label">Início</span><span class="detail-value">{{ formatDate(selectedSub.starts_at) }}</span></div>
            <div class="detail-item"><span class="detail-label">Fim</span><span class="detail-value">{{ formatDate(selectedSub.ends_at) }}</span></div>
            <div class="detail-item"><span class="detail-label">Dias restantes</span><span class="detail-value">{{ selectedSub.days_remaining ?? '—' }}</span></div>
            <div class="detail-item"><span class="detail-label">Slots extra</span><span class="detail-value">{{ selectedSub.extra_slots ?? 0 }}</span></div>
            <div class="detail-item"><span class="detail-label">Fonte</span><span class="detail-value">{{ selectedSub.source || '—' }}</span></div>
            <div class="detail-item"><span class="detail-label">Atribuído por</span><span class="detail-value">{{ selectedSub.granted_by?.name || '—' }}</span></div>
            <div class="detail-item" v-if="selectedSub.notes"><span class="detail-label">Notas</span><span class="detail-value">{{ selectedSub.notes }}</span></div>
          </div>

          <!-- Plan snapshot -->
          <div v-if="selectedSub.plan_snapshot" class="snapshot-section">
            <div class="snapshot-title">Snapshot do Plano (na altura da subscrição)</div>
            <div class="detail-grid">
              <div class="detail-item" v-for="(val, key) in selectedSub.plan_snapshot" :key="key">
                <span class="detail-label">{{ key }}</span>
                <span class="detail-value">{{ typeof val === 'boolean' ? (val ? 'Sim' : 'Não') : (val ?? '—') }}</span>
              </div>
            </div>
          </div>
        </q-card-section>

        <q-card-actions align="right" class="q-pa-lg q-pt-none">
          <q-btn flat label="Fechar" v-close-popup color="grey-6" />
        </q-card-actions>
      </q-card>
    </q-dialog>

  </div>
</template>

<script>
import { ref, computed, onBeforeUnmount, onMounted, watch } from 'vue';
import store from '@/vuex/store';
import Swal from 'sweetalert2';

export default {
  name: 'AdminSubscriptions',

  setup() {
    const loading = ref(false);
    const saving = ref(false);
    const currentPage = ref(1);
    const filterStatus = ref(null);
    const filterExpiring = ref(null);

    const assignDialog = ref(false);
    const extendDialog = ref(false);
    const detailDialog = ref(false);
    const paymentDialog = ref(false);

    const selectedSub = ref(null);
    const extendDays = ref(null);
    const extendNotes = ref('');
    const paymentSubmitting = ref(false);
    const paymentPolling = ref(false);
    const checkingPaymentReference = ref(null);
    const paymentStatus = ref(null);
    const paymentMessage = ref('');
    const paymentReference = ref('');
    const paymentAmount = ref(null);
    const paymentCurrency = ref('MZN');
    const paymentPlanPrices = ref([]);
    const paymentPlanPricesLoading = ref(false);
    let paymentPollTimer = null;
    let paymentPollAttempts = 0;

    const assignForm = ref({
      user: null, user_id: null,
      plan: null,
      selectedPrice: null,
      starts_at: '', ends_at: '',
      price: null, currency: 'MZN',
      extra_slots: 0, notes: '',
    });
    const paymentForm = ref({
      user: null,
      user_id: null,
      plan: null,
      duration_days: null,
      msisdn: '',
      idempotency_key: '',
      confirmAmount: false,
    });

    const allUsers = ref([]);
    const filteredUsers = ref([]);
    const planPrices = ref([]);
    const planPricesLoading = ref(false);

    const subscriptions = computed(() => store.state.subscriptions.adminSubscriptions);
    const meta = computed(() => store.state.subscriptions.adminSubscriptionsMeta);
    const activePlans = computed(() => store.getters['plans/activePlans']);
    const paymentPlans = computed(() => activePlans.value.filter((plan) => (
      plan.is_active !== false && !plan.is_negotiable
    )));
    const paymentDurationOptions = computed(() => paymentPlanPrices.value.map((price) => ({
      label: `${price.duration_days} dias — ${formatAmount(price.price, price.currency)}`,
      value: price.duration_days,
    })));
    const paymentPrice = computed(() => paymentPlanPrices.value.find(
      (price) => Number(price.duration_days) === Number(paymentForm.value.duration_days)
    ) || null);

    const statusOptions = [
      { label: 'Todas', value: null },
      { label: 'Pendente', value: 'pending' },
      { label: 'Activa', value: 'active' },
      { label: 'Expirada', value: 'expired' },
      { label: 'Cancelada', value: 'cancelled' },
    ];

    const statusLabel = (s, isQueued = false) => {
      if (s === 'pending' && isQueued) return 'Em espera';
      const map = { active: 'Activa', expired: 'Expirada', pending: 'Pendente', cancelled: 'Cancelada', inactive: 'Inactiva', paid: 'Pago', failed: 'Falhou' };
      return map[s] || s;
    };

    const paymentStatusLabel = (status) => ({
      paid: 'Confirmado',
      pending: 'Pendente',
      failed: 'Falhou',
      unknown: 'Por confirmar',
    }[status] || statusLabel(status));

    const formatAmount = (amount, currency = 'MZN') => (
      `${Number(amount || 0).toLocaleString('pt-MZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency || 'MZN'}`
    );

    const userLabel = (user) => user ? `${user.name || '—'} — ${user.email || ''}` : '';

    const formatDate = (d) => {
      if (!d) return '—';
      return new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    const newEndDate = computed(() => {
      if (!selectedSub.value?.ends_at || !extendDays.value) return null;
      const d = new Date(selectedSub.value.ends_at);
      d.setDate(d.getDate() + parseInt(extendDays.value));
      return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
    });

    const loadSubs = async (page = currentPage.value) => {
      loading.value = true;
      currentPage.value = page;
      try {
        await store.dispatch('subscriptions/getAdminSubscriptions', {
          status: filterStatus.value,
          expiring_in: filterExpiring.value,
          page,
        });
      } finally {
        loading.value = false;
      }
    };

    const filterUsers = (val, update) => {
      update(() => {
        const q = String(val || '').toLowerCase();
        filteredUsers.value = allUsers.value.filter(
          (u) => u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q)
        );
      });
    };

    const onPlanSelected = async (plan) => {
      assignForm.value.selectedPrice = null;
      assignForm.value.price = null;
      planPrices.value = [];
      if (!plan || plan.is_negotiable) return;
      planPricesLoading.value = true;
      try {
        planPrices.value = await store.dispatch('plans/getPlanPrices', plan.id);
        if (planPrices.value.length) {
          assignForm.value.selectedPrice = planPrices.value[0];
          assignForm.value.price = planPrices.value[0].price;
          assignForm.value.currency = planPrices.value[0].currency;
        }
      } catch {
        planPrices.value = [];
      } finally {
        planPricesLoading.value = false;
      }
    };

    const prefillPrice = (price) => {
      if (price) {
        assignForm.value.price = price.price;
        assignForm.value.currency = price.currency;
      }
    };

    const openAssign = async () => {
      assignForm.value = {
        user: null, user_id: null, plan: null, selectedPrice: null,
        starts_at: '', ends_at: '', price: null, currency: 'MZN', extra_slots: 0, notes: '',
      };
      if (!activePlans.value.length) {
        await store.dispatch('plans/getAdminPlans');
      }
      try {
        const users = await store.dispatch('admin/getUsers');
        allUsers.value = users || [];
        filteredUsers.value = allUsers.value;
      } catch {
        allUsers.value = [];
      }
      assignDialog.value = true;
    };

    const createIdempotencyKey = () => {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID();
      }
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (character) => {
        const random = Math.floor(Math.random() * 16);
        return (character === 'x' ? random : (random & 0x3) | 0x8).toString(16);
      });
    };

    const stopPaymentPolling = () => {
      if (paymentPollTimer) {
        window.clearInterval(paymentPollTimer);
        paymentPollTimer = null;
      }
      paymentPollAttempts = 0;
    };

    const paymentPayload = () => ({
      user_id: paymentForm.value.user_id,
      plan_id: paymentForm.value.plan?.id,
      duration_days: Number(paymentForm.value.duration_days),
      msisdn: paymentForm.value.msisdn,
      idempotency_key: paymentForm.value.idempotency_key,
    });

    const applyPaymentResult = (result, refreshList = true) => {
      paymentStatus.value = result.status || 'unknown';
      paymentMessage.value = result.message || 'O estado ainda não foi confirmado.';
      paymentReference.value = result.reference || paymentReference.value;
      paymentAmount.value = result.amount === undefined ? paymentAmount.value : Number(result.amount);
      paymentCurrency.value = result.currency || paymentCurrency.value;

      if (paymentStatus.value === 'pending' && paymentDialog.value && !paymentPollTimer) {
        paymentPollAttempts = 0;
        paymentPollTimer = window.setInterval(async () => {
          if (paymentPolling.value || !paymentReference.value) return;
          paymentPollAttempts += 1;
          if (paymentPollAttempts > 60) {
            stopPaymentPolling();
            paymentMessage.value = 'O M-Pesa ainda não confirmou o resultado. Consulte novamente antes de iniciar outra cobrança.';
            return;
          }
          await checkPaymentStatus(paymentReference.value);
        }, 5000);
      } else if (paymentStatus.value !== 'pending') {
        stopPaymentPolling();
      }

      if (refreshList) loadSubs();
    };

    const onPaymentUserSelected = (user) => {
      paymentForm.value.user_id = user?.id || null;
      if (user?.phone) paymentForm.value.msisdn = user.phone;
    };

    const onPaymentPlanSelected = async (plan) => {
      paymentForm.value.duration_days = null;
      paymentForm.value.confirmAmount = false;
      paymentPlanPrices.value = [];
      if (!plan) return;
      paymentPlanPricesLoading.value = true;
      try {
        const prices = await store.dispatch('plans/getPlanPrices', plan.id);
        paymentPlanPrices.value = (prices || []).filter((price) => (
          price.is_active !== false && Number(price.price) > 0
        ));
      } catch (error) {
        Swal.fire({
          title: 'Erro',
          text: error.response?.data?.message || 'Não foi possível carregar as durações e preços deste plano.',
          icon: 'error',
        });
      } finally {
        paymentPlanPricesLoading.value = false;
      }
    };

    const openPayment = async (sub = null) => {
      stopPaymentPolling();
      paymentStatus.value = null;
      paymentMessage.value = '';
      paymentReference.value = '';
      paymentAmount.value = null;
      paymentCurrency.value = 'MZN';
      paymentPlanPrices.value = [];
      paymentForm.value = {
        user: sub?.user || null,
        user_id: sub?.user?.id || null,
        plan: null,
        duration_days: null,
        msisdn: sub?.user?.phone || '',
        idempotency_key: createIdempotencyKey(),
        confirmAmount: false,
      };

      try {
        if (!activePlans.value.length) await store.dispatch('plans/getAdminPlans');
        if (!allUsers.value.length) {
          allUsers.value = await store.dispatch('admin/getUsers') || [];
        }
        filteredUsers.value = allUsers.value;
        if (sub?.user) {
          paymentForm.value.user = allUsers.value.find((user) => user.id === sub.user.id) || sub.user;
          paymentForm.value.user_id = paymentForm.value.user.id;
        }
        paymentDialog.value = true;
      } catch (error) {
        Swal.fire({
          title: 'Erro',
          text: error.response?.data?.message || 'Não foi possível carregar clientes e planos para a cobrança.',
          icon: 'error',
        });
      }
    };

    const closePaymentDialog = () => {
      stopPaymentPolling();
      paymentDialog.value = false;
    };

    const checkPaymentStatus = async (reference) => {
      if (!reference || paymentPolling.value) return;
      paymentPolling.value = true;
      checkingPaymentReference.value = reference;
      try {
        const result = await store.dispatch('subscriptions/getAdminPaymentStatus', reference);
        if (paymentDialog.value) applyPaymentResult(result, result.status !== 'pending');
        else await loadSubs();
      } catch (error) {
        if (paymentDialog.value) {
          paymentMessage.value = error.response?.data?.message || 'Não foi possível consultar o estado. A cobrança não será apresentada como paga sem confirmação.';
        }
      } finally {
        paymentPolling.value = false;
        checkingPaymentReference.value = null;
      }
    };

    const submitAdminPayment = async () => {
      if (paymentSubmitting.value) return;
      if (!paymentForm.value.user_id || !paymentForm.value.plan || !paymentPrice.value) {
        Swal.fire({ title: 'Campos obrigatórios', text: 'Seleccione o cliente, plano e duração.', icon: 'warning' });
        return;
      }
      if (!paymentForm.value.confirmAmount) {
        Swal.fire({ title: 'Confirme o valor', text: 'Confirme o montante antes de iniciar a cobrança.', icon: 'warning' });
        return;
      }
      if (!/^(?:\+?258)?8[2-7][0-9]{7}$/.test(paymentForm.value.msisdn.replace(/[\s\-().]/g, ''))) {
        Swal.fire({ title: 'Número inválido', text: 'Introduza um número de telemóvel moçambicano válido.', icon: 'warning' });
        return;
      }

      paymentSubmitting.value = true;
      try {
        const result = await store.dispatch('subscriptions/initiateAdminPayment', paymentPayload());
        applyPaymentResult(result);
      } catch (error) {
        const response = error.response?.data;
        if (response?.status) {
          applyPaymentResult(response);
        } else if (!error.response) {
          paymentStatus.value = 'unknown';
          paymentMessage.value = 'A ligação foi interrompida sem resultado. Reenvie com a mesma chave para consultar sem criar uma cobrança duplicada.';
        } else {
          Swal.fire({
            title: 'Não foi possível iniciar a cobrança',
            text: response?.message || 'Verifique os dados e tente novamente.',
            icon: 'error',
          });
        }
      } finally {
        paymentSubmitting.value = false;
      }
    };

    const retryAdminPayment = async () => {
      if (paymentStatus.value === 'failed') {
        paymentForm.value.idempotency_key = createIdempotencyKey();
        paymentReference.value = '';
        paymentAmount.value = null;
        paymentStatus.value = null;
        paymentMessage.value = '';
      }
      await submitAdminPayment();
    };

    const submitAssign = async () => {
      if (!assignForm.value.user_id || !assignForm.value.plan) {
        Swal.fire({ title: 'Campos obrigatórios', text: 'Seleccione o utilizador e o plano.', icon: 'warning' });
        return;
      }
      if (assignForm.value.plan.is_negotiable && !assignForm.value.price) {
        Swal.fire({ title: 'Preço obrigatório', text: 'Para planos Enterprise, introduza o preço acordado.', icon: 'warning' });
        return;
      }

      saving.value = true;
      try {
        const payload = {
          user_id: assignForm.value.user_id,
          plan_id: assignForm.value.plan.id,
          extra_slots: assignForm.value.extra_slots || 0,
          price: assignForm.value.price,
          currency: assignForm.value.currency,
          notes: assignForm.value.notes,
        };

        if (assignForm.value.plan.is_negotiable) {
          payload.starts_at = assignForm.value.starts_at;
          payload.ends_at = assignForm.value.ends_at;
        } else if (assignForm.value.selectedPrice) {
          payload.duration_days = assignForm.value.selectedPrice.duration_days;
        }

        await store.dispatch('subscriptions/createSubscription', payload);
        assignDialog.value = false;
        await loadSubs(1);
        Swal.fire({ title: 'Subscrição atribuída!', icon: 'success', timer: 1800, showConfirmButton: false });
      } catch (err) {
        Swal.fire({ title: 'Erro', text: err.response?.data?.message || 'Não foi possível atribuir a subscrição.', icon: 'error' });
      } finally {
        saving.value = false;
      }
    };

    const openExtend = (sub) => {
      selectedSub.value = sub;
      extendDays.value = null;
      extendNotes.value = '';
      extendDialog.value = true;
    };

    const submitExtend = async () => {
      if (!extendDays.value || extendDays.value <= 0) {
        Swal.fire({ title: 'Introduza o número de dias', icon: 'warning' });
        return;
      }
      saving.value = true;
      try {
        await store.dispatch('subscriptions/updateSubscription', {
          id: selectedSub.value.id,
          payload: { action: 'extend', duration_days: extendDays.value, notes: extendNotes.value },
        });
        extendDialog.value = false;
        await loadSubs();
        Swal.fire({ title: 'Subscrição estendida!', text: `Nova data de fim: ${newEndDate.value}`, icon: 'success', timer: 2500, showConfirmButton: false });
      } catch (err) {
        Swal.fire({ title: 'Erro', text: err.response?.data?.message || 'Não foi possível estender a subscrição.', icon: 'error' });
      } finally {
        saving.value = false;
      }
    };

    const confirmCancel = (sub) => {
      Swal.fire({
        title: 'Cancelar subscrição?',
        text: `A subscrição de "${sub.user?.name}" será cancelada.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#dc2626',
        cancelButtonColor: '#6b7280',
        confirmButtonText: 'Sim, cancelar',
        cancelButtonText: 'Voltar',
      }).then(async (result) => {
        if (!result.isConfirmed) return;
        try {
          await store.dispatch('subscriptions/updateSubscription', {
            id: sub.id,
            payload: { action: 'cancel' },
          });
          await loadSubs();
          Swal.fire({ title: 'Cancelada!', icon: 'success', timer: 1500, showConfirmButton: false });
        } catch (err) {
          Swal.fire({ title: 'Erro', text: err.response?.data?.message || 'Não foi possível cancelar.', icon: 'error' });
        }
      });
    };

    const openDetail = (sub) => {
      selectedSub.value = sub;
      detailDialog.value = true;
    };

    watch(filterStatus, () => loadSubs(1));
    watch(filterExpiring, () => loadSubs(1));
    watch(() => paymentForm.value.duration_days, () => {
      paymentForm.value.confirmAmount = false;
    });

    onMounted(async () => {
      await Promise.all([
        loadSubs(1),
        store.dispatch('plans/getAdminPlans'),
      ]);
    });

    onBeforeUnmount(stopPaymentPolling);

    return {
      loading, saving, currentPage, filterStatus, filterExpiring,
      assignDialog, extendDialog, detailDialog,
      paymentDialog, paymentForm, paymentPlans, paymentDurationOptions, paymentPrice,
      paymentSubmitting, paymentPolling, paymentStatus, paymentMessage, paymentReference,
      paymentAmount, paymentCurrency, paymentPlanPricesLoading, checkingPaymentReference,
      selectedSub, extendDays, extendNotes, newEndDate,
      assignForm, filteredUsers, activePlans, planPrices, planPricesLoading,
      subscriptions, meta,
      statusOptions, statusLabel, paymentStatusLabel, formatAmount, formatDate, userLabel,
      loadSubs, filterUsers, onPlanSelected, prefillPrice,
      openAssign, submitAssign,
      openPayment, closePaymentDialog, onPaymentUserSelected, onPaymentPlanSelected,
      submitAdminPayment, checkPaymentStatus, retryAdminPayment,
      openExtend, submitExtend,
      confirmCancel, openDetail,
    };
  },
};
</script>

<style scoped>
/* ── Page ─────────────────────────────────────────────── */
.subs-page { background: #f4f6fb; min-height: 100vh; }

/* ── Header ───────────────────────────────────────────── */
.page-header { background: #0d1b2a; padding: 20px 24px; }
.page-header-inner {
  display: flex; align-items: center; justify-content: space-between;
  max-width: 1200px; margin: 0 auto; flex-wrap: wrap; gap: 12px;
}
.page-title { font-size: 20px; font-weight: 700; color: #fff; }
.page-subtitle { font-size: 12px; color: rgba(212,175,55,.75); margin-top: 2px; }
.header-actions { display: flex; align-items: center; gap: 10px; }

.btn-primary {
  display: flex; align-items: center; padding: 9px 18px;
  background: #d4af37; color: #0d1b2a; border: none; border-radius: 10px;
  font-size: 13px; font-weight: 700; cursor: pointer;
}
.btn-primary:hover { background: #e2c252; }
.btn-payment {
  display: flex; align-items: center; padding: 9px 16px;
  background: rgba(255,255,255,.1); color: #fff; border: 1px solid rgba(212,175,55,.55);
  border-radius: 10px; font-size: 13px; font-weight: 700; cursor: pointer;
}
.btn-payment:hover { background: rgba(255,255,255,.18); }

/* ── Content ──────────────────────────────────────────── */
.content-wrapper { max-width: 1200px; margin: 28px auto; padding: 0 16px 60px; }

/* ── Filters ──────────────────────────────────────────── */
.filters-row { display: flex; gap: 12px; align-items: center; margin-bottom: 20px; flex-wrap: wrap; }
.filter-select { min-width: 180px; background: #fff; border-radius: 10px; }
.filter-input { min-width: 220px; background: #fff; border-radius: 10px; }
.btn-refresh {
  background: #fff; border: 1px solid #e2e8f0; border-radius: 10px;
  width: 40px; height: 40px; display: flex; align-items: center;
  justify-content: center; cursor: pointer; color: #64748b;
}
.spin { animation: spin 1s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

/* ── Subs list ────────────────────────────────────────── */
.subs-list { background: #fff; border-radius: 16px; border: 1px solid #e8ecf4; overflow-x: auto; }
.subs-table-head, .sub-row {
  display: grid;
  grid-template-columns: minmax(150px, 1.4fr) minmax(110px, 1fr) 90px 105px 85px 85px 70px 65px 100px 180px 120px 90px;
  min-width: 1450px;
  align-items: center; gap: 10px; padding: 12px 16px;
}
.subs-table-head {
  background: #f8fafc; font-size: 10.5px; font-weight: 700;
  color: #94a3b8; text-transform: uppercase; letter-spacing: .8px;
  border-bottom: 1px solid #e8ecf4;
}
.sub-row {
  border-bottom: 1px solid #f1f5f9; cursor: pointer;
  transition: background .12s;
}
.sub-row:last-child { border-bottom: none; }
.sub-row:hover { background: #f8fafc; }
.col-user, .col-plan { min-width: 0; }
.col-centre, .col-actions { text-align: center; }
.col-payment, .col-payment-action { text-align: center; min-width: 0; }
.user-name { font-size: 13.5px; font-weight: 600; color: #0f172a; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.user-email { font-size: 11px; color: #94a3b8; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.plan-name { font-size: 13px; font-weight: 500; color: #334155; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.col-actions { display: flex; gap: 4px; justify-content: center; }
.payment-amount { margin-top: 3px; color: #334155; font-size: 11px; font-weight: 700; }
.payment-reference { overflow: hidden; color: #94a3b8; font-size: 10px; text-overflow: ellipsis; white-space: nowrap; }
.payment-check-btn, .payment-retry-btn {
  padding: 2px 5px; border: 0; background: transparent; color: #0ea5e9;
  font-size: 10px; font-weight: 700; cursor: pointer;
}
.payment-check-btn:disabled, .payment-retry-btn:disabled { opacity: .55; cursor: wait; }
.payment-start-btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 5px;
  padding: 6px 9px; border: 1px solid rgba(212,175,55,.4); border-radius: 8px;
  background: rgba(212,175,55,.1); color: #8b6b00; font-size: 11px; font-weight: 700; cursor: pointer;
}
.payment-start-btn:hover { background: rgba(212,175,55,.2); }

/* ── Status badges ────────────────────────────────────── */
.status-badge {
  display: inline-flex; align-items: center; padding: 3px 9px;
  border-radius: 20px; font-size: 10.5px; font-weight: 600; letter-spacing: .3px; white-space: nowrap;
}
.status-badge--active    { background: rgba(34,197,94,.12);  color: #16a34a; }
.status-badge--expired   { background: rgba(239,68,68,.1);   color: #dc2626; }
.status-badge--pending   { background: rgba(245,158,11,.12); color: #d97706; }
.status-badge--cancelled { background: rgba(107,114,128,.1); color: #6b7280; }
.status-badge--inactive  { background: rgba(148,163,184,.12);color: #94a3b8; }
.status-badge--paid      { background: rgba(34,197,94,.12); color: #16a34a; }
.status-badge--failed    { background: rgba(239,68,68,.1); color: #dc2626; }

/* ── Action icons ─────────────────────────────────────── */
.action-icon {
  width: 30px; height: 30px; border-radius: 7px; border: 1px solid transparent;
  background: transparent; display: flex; align-items: center; justify-content: center;
  cursor: pointer; transition: background .15s; position: relative;
}
.action-icon--extend { color: #0ea5e9; }
.action-icon--extend:hover { background: rgba(14,165,233,.1); border-color: rgba(14,165,233,.2); }
.action-icon--danger { color: #dc2626; }
.action-icon--danger:hover { background: rgba(239,68,68,.1); border-color: rgba(239,68,68,.2); }

/* ── Empty ────────────────────────────────────────────── */
.empty-state { text-align: center; padding: 64px 16px; background: #fff; border-radius: 16px; border: 1px solid #e8ecf4; }
.empty-text { font-size: 15px; color: #94a3b8; margin-top: 12px; }

/* ── Pagination ───────────────────────────────────────── */
.pagination-row { display: flex; justify-content: center; margin-top: 24px; }

/* ── Skeleton ─────────────────────────────────────────── */
.skeleton-row { margin-bottom: 8px; }

/* ── Dialog ───────────────────────────────────────────── */
.dialog-header { background: #0d1b2a; padding: 18px 24px; }
.dialog-title { font-size: 16px; font-weight: 700; color: #fff; }
.form-row-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.payment-dialog { min-width: 500px; max-width: 620px; border-radius: 18px; }
.payment-amount-confirmation {
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  margin-bottom: 16px; padding: 12px 14px; border: 1px solid rgba(212,175,55,.35);
  border-radius: 10px; background: rgba(212,175,55,.08); color: #64748b; font-size: 13px;
}
.payment-amount-confirmation strong { color: #0d1b2a; font-size: 17px; }
.payment-result {
  display: flex; flex-direction: column; gap: 5px; margin-top: 12px; padding: 13px 15px;
  border: 1px solid #e2e8f0; border-radius: 10px; color: #475569; font-size: 12px;
}
.payment-result--pending, .payment-result--unknown { border-color: #fcd34d; background: #fffbeb; }
.payment-result--paid { border-color: #86efac; background: #f0fdf4; }
.payment-result--failed { border-color: #fca5a5; background: #fef2f2; }
.payment-result--paid strong { color: #15803d; }
.payment-result--failed strong { color: #b91c1c; }

/* ── Extend new date ──────────────────────────────────── */
.new-end-date {
  display: flex; align-items: center; background: rgba(34,197,94,.08);
  border: 1px solid rgba(34,197,94,.2); border-radius: 10px;
  padding: 10px 14px; font-size: 13px; color: #16a34a;
}

/* ── Detail grid ──────────────────────────────────────── */
.detail-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.detail-item { display: flex; flex-direction: column; gap: 2px; }
.detail-label { font-size: 10.5px; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: .6px; }
.detail-value { font-size: 13.5px; color: #0f172a; font-weight: 500; }
.snapshot-section { margin-top: 20px; }
.snapshot-title { font-size: 12px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: .8px; margin-bottom: 12px; padding-top: 16px; border-top: 1px solid #e8ecf4; }
@media (max-width: 560px) {
  .payment-dialog { min-width: 0; width: calc(100vw - 28px); }
}
</style>
